import { Link, useParams, useNavigate } from "react-router-dom";
import { SearchX } from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DeleteConfirmButton } from "@/components/delete-confirm-button";
import { DetailList, DetailRow } from "@/components/detail-list";
import { EntityButton } from "@/components/entity-button";
import { EmptyState } from "@/components/empty-state";
import { DetailSkeleton } from "@/components/loading-skeletons";
import { PageHeader, PageTitle } from "@/components/layout/page-header";
import { useWidgetQuery, useDeleteWidgetMutation } from "@/hooks/use-widgets";
import { useDataSourceQuery } from "@/hooks/use-data-sources";

const BREADCRUMBS = [{ label: "Widgets", to: "/widgets" }];

export function WidgetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: widget, isLoading: widgetLoading, error: widgetError, refetch } = useWidgetQuery(id!);
  const deleteMutation = useDeleteWidgetMutation();

  const handleDelete = () => {
    deleteMutation.mutate(id!, {
      onSuccess: () => {
        navigate("/widgets", { replace: true });
      },
    });
  };
  const { data: dataSource, isLoading: dataSourceLoading } = useDataSourceQuery(
    widget?.data_source_id ?? ""
  );

  const isLoading = widgetLoading || dataSourceLoading;

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col gap-6">
        <PageHeader breadcrumbs={BREADCRUMBS} title="Widget" />
        <DetailSkeleton />
      </div>
    );
  }

  if (widgetError || !widget) {
    return (
      <div className="flex flex-1 flex-col gap-6">
        <PageHeader breadcrumbs={BREADCRUMBS} title="Widget" />
        <EmptyState
          icon={SearchX}
          title="Widget Not Found"
          description="The widget you are looking for does not exist."
        >
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6">
      <PageHeader
        breadcrumbs={BREADCRUMBS}
        title={widget.name}
        actions={
          <>
            <EntityButton entityName="Widget" mode="edit" to={`/widgets/${id}/edit`} />
            <DeleteConfirmButton
              entityName="Widget"
              name={widget.name}
              onConfirm={handleDelete}
              mutation={deleteMutation}
            />
          </>
        }
      />

      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
        <PageTitle title={widget.name} />

        <Card>
          <CardHeader>
            <CardTitle>Widget Details</CardTitle>
          </CardHeader>
          <CardContent>
            <DetailList>
              <DetailRow label="Status">
                {widget.published ? (
                  <Badge variant="success">Published</Badge>
                ) : (
                  <Badge variant="secondary">Draft</Badge>
                )}
              </DetailRow>
              <DetailRow label="Data Source">
                {widget.data_source_id ? (
                  <Link
                    to={`/widgets/data-sources/${widget.data_source_id}`}
                    className="-mx-1 -my-2 px-1 py-2 text-primary underline-offset-4 hover:underline"
                    title={dataSource?.name || widget.data_source_id}
                  >
                    {dataSource?.name || widget.data_source_id}
                  </Link>
                ) : (
                  <span className="font-normal text-muted-foreground">None</span>
                )}
              </DetailRow>
            </DetailList>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
